import { MenuPointAction } from '@agilox/common';
import { isRouteMenuPoint, MenuPoint, NavColor, NavColors } from '@agilox/ui-common';
import {
	afterRenderEffect,
	Component,
	computed,
	ElementRef,
	HostListener,
	input,
	output,
	viewChild,
	ChangeDetectionStrategy,
} from '@angular/core';

@Component({
	selector: 'ui-sub-nav',
	templateUrl: './sub-nav.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class SubNavComponent {
	menuItem = input<MenuPoint | undefined>();
	navColor = input<NavColor>(NavColors.Dark);
	currentUrl = input<string>('');

	itemClick = output<MenuPointAction>();

	subNav = viewChild<ElementRef>('subNav');

	positionTop = computed(() => {
		if (!this.menuItem()) {
			return '0px';
		}

		let el = document.querySelector('[data-id="' + this.menuItem()!.url + '"]');
		if (el) {
			return (el.getBoundingClientRect().top - window.scrollY + 8).toString() + 'px';
		}
		return '0px';
	});

	constructor() {
		// Keep the flyout on screen: re-run whenever the active menu (and thus its
		// position/height) changes. offsetHeight is read instead of getBoundingClientRect
		// because it is unaffected by the panel's top/transform transition.
		afterRenderEffect(() => {
			this.positionTop();
			this.repositionPanel();
		});
	}

	@HostListener('window:resize')
	protected onResize() {
		this.repositionPanel();
	}

	/**
	 * If the panel would overflow the bottom of the viewport, shift it up just enough
	 * to fit. When it is taller than the viewport it stays pinned near the top and its
	 * body scrolls (via the panel's max-height + overflow-y-auto).
	 */
	private repositionPanel(): void {
		const el = this.subNav()?.nativeElement as HTMLElement | undefined;
		if (!el) {
			return;
		}

		const margin = 32;
		const top = parseFloat(this.positionTop());
		const overflow = top + el.offsetHeight - (window.innerHeight - margin);
		const shift = overflow > 0 ? Math.min(overflow, Math.max(0, top - margin)) : 0;

		el.style.transform = shift > 0 ? `translateY(-${shift}px)` : '';
	}

	@HostListener('keydown', ['$event'])
	onKeyDown(event: KeyboardEvent) {
		const target = event.target as HTMLElement;
		if (!target.closest('ui-sub-nav')) {
			return;
		}

		const currentButton = target.closest('a, button') as HTMLElement | null;

		if (!currentButton) {
			return;
		}

		switch (event.key) {
			case 'ArrowDown':
				event.preventDefault();
				this.navigateDown(currentButton);
				break;
			case 'ArrowUp':
				event.preventDefault();
				this.navigateUp(currentButton);
				break;
			case 'ArrowLeft':
				event.preventDefault();
				this.navigateLeft();
				break;
			case 'Enter':
			case ' ':
				event.preventDefault();
				currentButton.click();
				break;
			case 'Escape':
				event.preventDefault();
				this.handleEscape();
				break;
			case 'Home':
				event.preventDefault();
				this.navigateToFirst();
				break;
			case 'End':
				event.preventDefault();
				this.navigateToLast();
				break;
		}
	}

	private navigateDown(currentButton: HTMLElement) {
		const allButtons = this.getAllSubNavButtons();
		const currentIndex = allButtons.indexOf(currentButton);
		const nextIndex = Math.min(currentIndex + 1, allButtons.length - 1);
		allButtons[nextIndex]?.focus();
	}

	private navigateUp(currentButton: HTMLElement) {
		const allButtons = this.getAllSubNavButtons();
		const currentIndex = allButtons.indexOf(currentButton);

		if (currentIndex === 0) {
			setTimeout(() => {
				const mainNavButton = document.querySelector(
					`ui-nav-list-entry[data-id="${this.menuItem()?.url}"] a, ui-nav-list-entry[data-id="${this.menuItem()?.url}"] button`
				) as HTMLElement;
				if (mainNavButton) {
					mainNavButton.focus();
				}
			}, 10);
		} else {
			const prevIndex = Math.max(currentIndex - 1, 0);
			allButtons[prevIndex]?.focus();
		}
	}

	private navigateLeft() {
		setTimeout(() => {
			const mainNavButton = document.querySelector(
				`ui-nav-list-entry[data-id="${this.menuItem()?.url}"] a, ui-nav-list-entry[data-id="${this.menuItem()?.url}"] button`
			) as HTMLElement;
			if (mainNavButton) {
				mainNavButton.focus();
			}
		}, 10);
	}

	private navigateToFirst() {
		const allButtons = this.getAllSubNavButtons();
		allButtons[0]?.focus();
	}

	private navigateToLast() {
		const allButtons = this.getAllSubNavButtons();
		allButtons[allButtons.length - 1]?.focus();
	}

	private handleEscape() {
		setTimeout(() => {
			const mainNavButton = document.querySelector(
				`ui-nav-list-entry[data-id="${this.menuItem()?.url}"] a, ui-nav-list-entry[data-id="${this.menuItem()?.url}"] button`
			) as HTMLElement;
			if (mainNavButton) {
				mainNavButton.focus();
			}
		}, 10);
	}

	private getAllSubNavButtons(): HTMLElement[] {
		return Array.from(this.subNav()?.nativeElement.querySelectorAll('a, button') || []);
	}

	clickItem(item: MenuPoint) {
		this.itemClick.emit({ click: false, menuPoint: item });
	}

	isRouteItem(item: MenuPoint): boolean {
		return isRouteMenuPoint(item);
	}

	getSubmenus(item: MenuPoint) {
		return item.submenus?.filter((submenu) => !submenu.hidden);
	}

	isSubmenuActive(submenu: MenuPoint): boolean {
		if (submenu.active) {
			return true;
		}
		if (!submenu.url) {
			return false;
		}
		const urlWithoutParams = this.currentUrl().split('?')[0];
		return urlWithoutParams === submenu.url;
	}
}
